import {
	COOKIE_CONSENT_EVENT,
	getCookiePreferences,
} from "@/utils/cookieConsent"

let analyticsStarted = false
let analyticsRouterHookInstalled = false
let mapsPromise = null

const analyticsId = () => process.env.VUE_APP_GOOGLE_ANALYTICS_ID

const analyticsDisableKey = () => `ga-disable-${analyticsId()}`

const sendPageView = (route) => {
	if (!analyticsStarted || !getCookiePreferences().analytics || typeof window.gtag !== "function") return

	window.gtag("event", "page_view", {
		page_title: document.title,
		page_location: window.location.href,
		page_path: route.fullPath,
	})
}

const clearGoogleAnalyticsCookies = () => {
	const cookieNames = document.cookie
		.split(";")
		.map((cookie) => cookie.split("=")[0].trim())
		.filter((name) => name === "_ga" || name === "_gid" || name === "_gat" || name.startsWith("_ga_"))

	const hostParts = window.location.hostname.split(".")
	const domains = [window.location.hostname]
	if (hostParts.length >= 2) domains.push(`.${hostParts.slice(-2).join(".")}`)

	cookieNames.forEach((name) => {
		document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`
		domains.forEach((domain) => {
			document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}; SameSite=Lax`
		})
	})
}

const stopAnalytics = () => {
	if (!analyticsId()) return
	window[analyticsDisableKey()] = true
	if (typeof window.gtag === "function") {
		window.gtag("consent", "update", { analytics_storage: "denied" })
	}
	clearGoogleAnalyticsCookies()
}

const startAnalytics = (router) => {
	if (!analyticsId()) return

	window[analyticsDisableKey()] = false
	if (!analyticsStarted) {
		window.dataLayer = window.dataLayer || []
		window.gtag = window.gtag || function gtag() {
			window.dataLayer.push(arguments)
		}
		window.gtag("consent", "default", { analytics_storage: "granted" })
		window.gtag("js", new Date())
		window.gtag("config", analyticsId(), {
			anonymize_ip: true,
			send_page_view: false,
		})

		const script = document.createElement("script")
		script.async = true
		script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(analyticsId())}`
		script.dataset.consentManaged = "analytics"
		document.head.appendChild(script)
		analyticsStarted = true
	}
	window.gtag("consent", "update", { analytics_storage: "granted" })

	if (!analyticsRouterHookInstalled) {
		router.afterEach((to) => sendPageView(to))
		analyticsRouterHookInstalled = true
	}
	sendPageView(router.currentRoute)
}

export const initializeConsentManagedAnalytics = (router) => {
	const applyPreferences = (preferences) => {
		if (preferences.analytics) startAnalytics(router)
		else stopAnalytics()
	}

	applyPreferences(getCookiePreferences())
	window.addEventListener(COOKIE_CONSENT_EVENT, (event) => applyPreferences(event.detail.preferences))
}

export const loadConsentManagedGoogleMaps = () => {
	if (!getCookiePreferences().embeddedMedia) return Promise.resolve(null)
	if (window.google?.maps) return Promise.resolve(window.google.maps)
	if (mapsPromise) return mapsPromise

	mapsPromise = new Promise((resolve) => {
		const callbackName = `slmathGoogleMapsReady${Date.now()}`
		window[callbackName] = () => {
			delete window[callbackName]
			resolve(window.google.maps)
		}

		const script = document.createElement("script")
		script.async = true
		script.defer = true
		script.dataset.consentManaged = "third-party-content"
		script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
			process.env.VUE_APP_GOOGLE_API_PLACE || "",
		)}&libraries=places&v=weekly&callback=${callbackName}`
		script.onerror = () => {
			delete window[callbackName]
			mapsPromise = null
			resolve(null)
		}
		document.head.appendChild(script)
	})

	return mapsPromise
}
